import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
} from '@material-ui/core';
import { toast } from 'react-toastify';
import { getAllReasons, markRelavancy } from '../../../services/apiService';
import { Visibility } from '@material-ui/icons';

const RelevancyRenderer = (props) => {
  const { setCurrentRowData, setIsOpen, isOpen, rowId, currentRowData } = props;
  const [isRelevant, setIsRelevant] = useState(() => {
    const value = props.data?.relevant;
    if (value === 'selected') return 'selected';
    if (value === 'rejected') return 'rejected';
    if (value === 'handpicked') return 'handpicked';
    return null;
  });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [remarks, setRemarks] = useState('');
  const [reason, setReason] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [reasonOptions, setReasonOptions] = useState([]);

  // Fetch reasons when dialog opens
  useEffect(() => {
    const fetchReasons = async () => {
      if (dialogOpen) {
        try {
          const response = await getAllReasons();
          if (response?.data?.REJECT_REASONS) {
            setReasonOptions(response.data.REJECT_REASONS);
          }
        } catch (error) {
          console.error('Error fetching reasons:', error);
          toast.error('Failed to load rejection reasons');
        }
      }
    };

    fetchReasons();
  }, [dialogOpen]);

  const handleApiCall = async (payload) => {
    setIsLoading(true);
    try {
      const response = await markRelavancy(payload);
      toast.success('Marked successfully');
      return response;
    } catch (error) {
      toast.error('Failed to mark relevancy');
      console.error('Error marking relevancy:', error);
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  const handleEyeClick = () => {
    if (props?.data) {
      setCurrentRowData?.(props?.data);
      setIsOpen?.(true);
    }
  };

  const handleRelavancyActions = async (relevancyType) => {
    if (isLoading) return;

    // For non-relevant case, validate reason
    if (relevancyType === 'rejected' && !reason) {
      toast.warning('Please select a reason');
      return;
    }

    // Construct payload
    const payload = {
      id: props?.data?.id || rowId,
      relevant: relevancyType,
      ...(relevancyType === 'rejected' ? { feedback: reason, remarks: remarks } : {}),
    };

    // Make API call
    const response = await handleApiCall(payload);

    if (response) {
      props.setValue?.({
        isRelevant: relevancyType !== 'rejected',
        remarks: relevancyType === 'rejected' ? remarks : '',
        reason: relevancyType === 'rejected' ? reason : '',
      });

      if (relevancyType === 'rejected') {
        setDialogOpen(false);
        setRemarks('');
        setReason('');
      }
    } else {
      setIsRelevant(null);
    }
  };

  const handleRelevancyClick = (value) => {
    setIsRelevant(value);
    if (value === 'rejected') {
      setDialogOpen(true);
    } else {
      handleRelavancyActions(value);
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setRemarks('');
    setReason('');
    setIsRelevant(null);
  };

  useEffect(() => {
    const value = props.data?.relevant || currentRowData?.relevant;
    if (value === 'selected') setIsRelevant('selected');
    else if (value === 'rejected') setIsRelevant('rejected');
    else if (value === 'handpicked') setIsRelevant('handpicked');
    else setIsRelevant(null);
  }, [props.data?.relevant, currentRowData?.relevant]);

  return (
    <div className='flex flex-col items-start m-1'>
      <div className='flex gap-2'>
        <button
          onClick={() => handleRelevancyClick('selected')}
          className={`rounded px-3  text-white ${isRelevant === 'selected' ? 'bg-green-600' : 'bg-gray-300'
            }`}
          disabled={isLoading}
        >
          Relevant
        </button>
        <button
          onClick={() => handleRelevancyClick('handpicked')}
          className={`rounded px-3 text-white ${isRelevant === 'handpicked' ? 'bg-blue-600' : 'bg-gray-300'
            }`}
          disabled={isLoading}
        >
          Handpicked
        </button>
        <button
          onClick={() => handleRelevancyClick('rejected')}
          className={`rounded px-3 text-white ${isRelevant === 'rejected' ? 'bg-red-600' : 'bg-gray-300'
            }`}
          disabled={isLoading}
        >
          Not Relevant
        </button>

        {isOpen ? (
          <></>
        ) : (
          <button
            onClick={handleEyeClick}
            className='ml-2 text-gray-700 hover:text-blue-600'
            title='View details'
          >
            <Visibility />
          </button>
        )}
      </div>

      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle>Reason for Non-Relevance</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Please provide your reason and remarks for marking this data as not
            relevant.
          </DialogContentText>

          <FormControl fullWidth margin='dense' variant='outlined'>
            <InputLabel>Reason</InputLabel>
            <Select
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              label='Reason'
            >
              {reasonOptions.map((option, index) => (
                <MenuItem key={index} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            margin='dense'
            label='Remarks'
            type='text'
            fullWidth
            variant='outlined'
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseDialog}
            color='primary'
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={() => handleRelavancyActions('rejected')}
            color='primary'
            variant='contained'
            disabled={isLoading || !reason}
          >
            {isLoading ? 'Submitting...' : 'Submit'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default RelevancyRenderer;
